import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { API } from "@/lib/api";
const MAX_MB = 8;

const ArtUpload = ({ onQuoteClick }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [fileName, setFileName] = useState("");

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Envie um arquivo de imagem (PNG, JPG ou SVG).");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Arquivo maior que ${MAX_MB} MB. Reduza e tente novamente.`);
      return;
    }
    setFileName(file.name);
    setLoading(true);
    setPreviews([]);
    try {
      const form = new FormData();
      form.append("file", file);
      const resp = await fetch(`${API}/mockup/preview`, { method: "POST", body: form });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setPreviews(data.previews || []);
      toast.success("Mock-ups gerados! Confira abaixo.");
    } catch (e) {
      toast.error("Falha ao gerar mock-up. Tente novamente ou envie via WhatsApp.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFile = (e) => upload(e.target.files?.[0]);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <section
      id="mockup-online"
      data-testid="art-upload-section"
      className="py-24 lg:py-32 bg-[#F5F5F5] overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="max-w-3xl mb-12">
          <span
            className="inline-block text-[0.72rem] font-semibold uppercase tracking-[0.28em] text-[var(--mn-gold)] mb-4"
          >
            Mock-up automático · Grátis
          </span>
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.1] mb-6 text-[var(--mn-black)]">
            Envie sua arte e veja
            <br />
            <span className="italic text-[var(--mn-gold)]">como sua marca fica</span> em segundos
          </h2>
          <p className="text-[var(--mn-dark)] text-sm md:text-base leading-relaxed">
            Faça upload do seu logotipo (PNG ou JPG com fundo transparente de preferência) e nós compomos automaticamente em três dos nossos produtos: caneca, camiseta e sacola de algodão. Sem compromisso.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Upload zone */}
          <div className="lg:col-span-2">
            <div
              data-testid="art-upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`cursor-pointer flex flex-col items-center justify-center min-h-[280px] p-8 rounded-lg border-2 border-dashed transition-all duration-300 ${
                dragging
                  ? "border-[var(--mn-gold)] bg-white shadow-lg"
                  : "border-[var(--mn-black)]/20 bg-white/60 hover:border-[var(--mn-gold)]/60 hover:bg-white"
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleFile}
                className="hidden"
                data-testid="art-upload-input"
              />
              <div className="w-16 h-16 rounded-full bg-[var(--mn-gold)]/10 flex items-center justify-center mb-6">
                <i className="fa-solid fa-cloud-arrow-up text-2xl text-[var(--mn-gold)]" />
              </div>
              <div className="font-serif-display font-bold text-lg md:text-xl text-[var(--mn-black)] mb-2 text-center">
                Arraste sua arte aqui
              </div>
              <div className="text-sm text-[var(--mn-dark)]/70 text-center mb-4">
                ou clique para selecionar do computador
              </div>
              <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[var(--mn-dark)]/50 text-center">
                PNG · JPG · SVG · até {MAX_MB} MB
              </div>
              {fileName && (
                <div className="mt-4 text-xs text-[var(--mn-gold)] font-medium truncate max-w-full" data-testid="art-upload-filename">
                  <i className="fa-solid fa-file-image mr-1.5" />
                  {fileName}
                </div>
              )}
            </div>

            <div className="mt-6 text-xs text-[var(--mn-dark)]/60 leading-relaxed">
              <i className="fa-solid fa-lock mr-1.5 text-[var(--mn-gold)]" />
              Sua arte não é armazenada nem compartilhada — usamos apenas para gerar o preview.
            </div>
          </div>

          {/* Previews */}
          <div className="lg:col-span-3">
            {loading && (
              <div
                data-testid="art-upload-loading"
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-lg bg-white/60 border border-[var(--mn-black)]/[0.08] animate-pulse flex items-center justify-center"
                  >
                    <i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--mn-gold)]/40" />
                  </div>
                ))}
              </div>
            )}

            {!loading && previews.length > 0 && (
              <>
                <div
                  data-testid="art-upload-previews"
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                  {previews.map((p) => (
                    <div
                      key={p.slug}
                      data-testid={`preview-${p.slug}`}
                      className="group aspect-square rounded-lg overflow-hidden bg-white border border-[var(--mn-black)]/[0.08] transition-all duration-300 hover:border-[var(--mn-gold)]/40 hover:shadow-lg"
                    >
                      <img
                        src={p.data_url}
                        alt={`Preview em ${p.label}`}
                        className="w-full h-full object-contain p-3 transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {previews.map((p) => (
                    <div
                      key={p.slug + "-label"}
                      className="text-center text-[0.7rem] uppercase tracking-[0.22em] font-semibold text-[var(--mn-dark)]"
                    >
                      {p.label}
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-[var(--mn-black)]/10 pt-6">
                  <p className="text-sm text-[var(--mn-dark)] leading-relaxed">
                    Gostou? Solicite um orçamento e enviamos artes de alta qualidade em qualquer produto.
                  </p>
                  <button
                    type="button"
                    onClick={() => onQuoteClick?.()}
                    data-testid="art-upload-quote-cta"
                    className="btn-mn-primary !py-3 !px-6 whitespace-nowrap"
                  >
                    <i className="fa-regular fa-paper-plane" />
                    Fechar orçamento
                  </button>
                </div>
              </>
            )}

            {!loading && previews.length === 0 && (
              <div className="flex items-center justify-center min-h-[280px] p-8 rounded-lg bg-white/40 border border-[var(--mn-black)]/[0.06]">
                <div className="text-center max-w-sm">
                  <div className="text-4xl text-[var(--mn-gold)]/40 mb-4">
                    <i className="fa-solid fa-shirt" />
                    <i className="fa-solid fa-mug-saucer ml-4" />
                    <i className="fa-solid fa-bag-shopping ml-4" />
                  </div>
                  <div className="text-sm text-[var(--mn-dark)]/60 leading-relaxed">
                    Envie um logotipo para ver o preview em caneca, camiseta e sacola personalizadas.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArtUpload;
